import { useEffect, useMemo, useState } from "react";
import {
  fetchLoveLanguagesMeta,
  fetchLoveLanguagesQuestions,
  submitLoveLanguagesAnswers,
} from "../api/loveLanguagesApi";
import { GuestMatchHint } from "../components/WeChatLoginButton";

const STORAGE_KEY = "love_languages_result_v1";

export default function LoveLanguagesSection() {
  const [meta, setMeta] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState("intro"); // intro | quiz | result
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, q] = await Promise.all([
          fetchLoveLanguagesMeta(),
          fetchLoveLanguagesQuestions(),
        ]);
        if (cancelled) return;
        setMeta(m);
        setQuestions(q?.items || []);
        try {
          const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
          if (saved?.breakdown) {
            setResult(saved);
          }
        } catch {
          /* ignore */
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "加载失败");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const current = questions[index];
  const progress = questions.length
    ? Math.round((Object.keys(answers).length / questions.length) * 100)
    : 0;

  const primary = useMemo(() => {
    if (!result?.breakdown?.length) return null;
    return result.breakdown[0];
  }, [result]);

  const startQuiz = () => {
    setAnswers({});
    setIndex(0);
    setResult(null);
    setError("");
    setPhase("quiz");
    localStorage.removeItem(STORAGE_KEY);
  };

  const pick = async (side) => {
    if (!current || busy) return;
    const next = { ...answers, [String(current.id)]: side };
    setAnswers(next);

    if (index < questions.length - 1) {
      setIndex((i) => i + 1);
      return;
    }

    setBusy(true);
    setError("");
    try {
      const data = await submitLoveLanguagesAnswers(next);
      setResult(data);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setPhase("result");
    } catch (e) {
      setError(e.message || "提交失败");
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => {
    if (index <= 0) return;
    setIndex((i) => i - 1);
  };

  if (loading) {
    return <p className="text-center text-sm text-gray-400 py-16">加载中…</p>;
  }

  return (
    <div className="w-full max-w-lg mx-auto space-y-5">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {phase === "intro" && (
        <section className="space-y-6 animate-[fadeIn_0.35s_ease]">
          <header className="text-center space-y-3">
            <div className="mx-auto w-36 h-44 rounded-2xl bg-gradient-to-br from-amber-400 via-rose-400 to-violet-500 shadow-lg flex flex-col items-center justify-center text-white rotate-1">
              <span className="text-3xl mb-2">💬</span>
              <p className="font-bold text-sm tracking-wide">五种爱语</p>
            </div>
            <h2 className="text-2xl font-extrabold text-gray-800">
              {meta?.title || "爱的五种语言"}
            </h2>
            <p className="text-rose-600 font-medium text-sm">
              {meta?.subtitle}
            </p>
            <p className="text-sm text-gray-500">{meta?.tagline}</p>
          </header>

          <div className="bg-white/90 rounded-3xl border border-rose-100 p-5 space-y-3">
            <p className="text-sm font-semibold text-gray-800">五种语言一览</p>
            <ul className="space-y-2">
              {(meta?.languages || []).map((lang) => (
                <li
                  key={lang.key}
                  className="flex gap-3 items-start text-sm text-gray-600"
                >
                  <span
                    className="mt-1 w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: lang.color }}
                  />
                  <span>
                    <span className="font-medium text-gray-800">{lang.name}</span>
                    <span className="text-gray-400"> · {lang.short}</span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-400 pt-2 border-t border-gray-50">
              共 {meta?.question_count || 30} 道迫选题，每题二选一，约 4–6 分钟。
              游客可直接测完。{meta?.disclaimer}
            </p>
          </div>

          <GuestMatchHint redirect="/love-map?tab=languages" />

          <button
            type="button"
            onClick={startQuiz}
            className="w-full py-3.5 rounded-full bg-rose-500 text-white font-semibold shadow-lg shadow-rose-200 hover:bg-rose-600"
          >
            开始测评
          </button>

          {result?.breakdown && (
            <button
              type="button"
              onClick={() => setPhase("result")}
              className="w-full py-3 rounded-full border border-violet-200 text-violet-700 text-sm font-medium"
            >
              查看上次结果
            </button>
          )}
        </section>
      )}

      {phase === "quiz" && current && (
        <section className="space-y-4 animate-[fadeIn_0.25s_ease]">
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-500">
              <span>
                第 {index + 1} / {questions.length} 题
              </span>
              <span>{progress}%</span>
            </div>
            <div className="h-2 bg-white rounded-full border border-rose-100 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-rose-400 to-violet-500 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="rounded-3xl bg-white shadow-xl border border-rose-50 p-5 md:p-6 space-y-4">
            <p className="text-xs text-gray-400">哪一种更让你感到被爱？</p>
            <h3 className="text-lg font-bold text-gray-800">二选一</h3>
            <div className="space-y-3">
              {[
                ["a", current.option_a],
                ["b", current.option_b],
              ].map(([key, text]) => (
                <button
                  key={key}
                  type="button"
                  disabled={busy}
                  onClick={() => pick(key)}
                  className={`w-full text-left px-4 py-4 rounded-2xl border transition active:scale-[0.98] ${
                    answers[String(current.id)] === key
                      ? "border-rose-500 bg-rose-50 text-rose-800"
                      : "border-gray-200 hover:border-rose-300 bg-white"
                  }`}
                >
                  <span className="font-semibold mr-2 uppercase">{key}.</span>
                  {text}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={goBack}
              disabled={index === 0 || busy}
              className="text-sm text-gray-500 disabled:opacity-40"
            >
              ← 上一题
            </button>
            <button
              type="button"
              onClick={() => setPhase("intro")}
              className="text-sm text-gray-400"
            >
              退出
            </button>
          </div>
          {busy && (
            <p className="text-center text-sm text-gray-400">正在出结果…</p>
          )}
        </section>
      )}

      {phase === "result" && result && primary && (
        <section className="space-y-5 animate-[fadeIn_0.35s_ease]">
          <header className="text-center space-y-2">
            <p className="text-sm text-violet-500 font-medium">你的主要爱语</p>
            <h2
              className="text-3xl font-extrabold"
              style={{ color: primary.color }}
            >
              {result.primary_name}
            </h2>
            {result.bilingual && result.secondary_name ? (
              <p className="text-sm text-amber-700 bg-amber-50 rounded-full inline-block px-3 py-1">
                双语倾向：也与「{result.secondary_name}」并列领先
              </p>
            ) : result.secondary_name ? (
              <p className="text-sm text-gray-500">
                次要爱语：{result.secondary_name}
              </p>
            ) : null}
            <p className="text-sm text-gray-600 leading-relaxed px-2">
              {primary.summary}
            </p>
          </header>

          <div className="bg-white rounded-3xl border border-rose-100 p-5 space-y-3">
            <p className="text-sm font-semibold text-gray-800">得分分布</p>
            {result.breakdown.map((item) => (
              <div key={item.key} className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="font-medium text-gray-700">{item.name}</span>
                  <span className="text-gray-500">
                    {item.score} 分 · {item.percent}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${item.percent}%`,
                      background: item.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="bg-rose-50 rounded-2xl p-4 space-y-2">
              <h3 className="text-sm font-semibold text-rose-800">希望对方这样爱你</h3>
              <ul className="text-xs text-rose-900 space-y-1.5">
                {(primary.tips_receive || []).map((t) => (
                  <li key={t}>· {t}</li>
                ))}
              </ul>
            </div>
            <div className="bg-violet-50 rounded-2xl p-4 space-y-2">
              <h3 className="text-sm font-semibold text-violet-800">你可以这样表达爱</h3>
              <ul className="text-xs text-violet-900 space-y-1.5">
                {(primary.tips_give || []).map((t) => (
                  <li key={t}>· {t}</li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-[11px] text-gray-400 leading-relaxed">
            {result.disclaimer}
          </p>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={startQuiz}
              className="w-full py-3 rounded-full bg-rose-500 text-white font-semibold"
            >
              再测一次
            </button>
            <button
              type="button"
              onClick={() => setPhase("intro")}
              className="w-full py-3 rounded-full border border-gray-200 text-gray-600 text-sm"
            >
              返回介绍
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
